import React from 'react';
import { AttachedService } from '@jbhi-fi/components/src/Cart/AttachedService/AttachedService';
import { formatMoney } from '@jbhi-fi/lanyard-ui/global/utils';
import { styles } from './CartService.css';
import { AttachedServiceEntry } from '../cartLineServices';
import { AttachedServiceProps } from '@jbhi-fi/components/src/Cart/AttachedService/AttachedService';
import { renderInstallModule, renderServicesModule, ServicesModuleConfig } from './ServicesCarousel';

type AttachedServiceVariant = 'cart' | 'miniCart';

export interface AttachedServicesBlockProps {
  lineId: string;
  variant: AttachedServiceVariant;
  isNoLongerAvailable?: boolean;
  attachedInstallServices: Array<AttachedServiceEntry>;
  attachedService?: AttachedServiceProps;
  shouldRenderDefaultAttachedService: boolean;
  removeInstallServices?: () => void;
  removeAttachedService?: () => void;
  servicesModuleConfig: ServicesModuleConfig;
}

const renderAttachedInstallService = (
  lineId: string,
  variant: AttachedServiceVariant,
  isNoLongerAvailable: boolean | undefined,
  attachedInstallServices: Array<AttachedServiceEntry>,
  removeInstallServices?: () => void
) => {
  if (!attachedInstallServices.length) return null;
  const generalTitle =
    attachedInstallServices
      .map(s => (s?.serviceData?.generalTitle ?? '').trim())
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)[0] ?? '';
  const totalPrice = attachedInstallServices.reduce((sum, entry) => sum + entry.cartItem.line_price / 100, 0);

  const subtextLines = attachedInstallServices.map(entry => {
    const label = entry.serviceData.offeringName ?? entry.cartItem.product_title ?? '';
    const priceValue = entry.cartItem.line_price / 100;
    return `• ${label} ${priceValue === 0 ? 'FREE' : priceValue ? formatMoney(priceValue) : ''}`;
  });

  const firstImage = attachedInstallServices.find(entry => entry.cartItem.image)?.cartItem.image;
  const isValid = attachedInstallServices.every(entry => entry.cartItem.isValidService !== false);

  return (
    <AttachedService
      key={`tvinstall-attached-${lineId}`}
      name={generalTitle}
      subtext={subtextLines.join('\n')}
      price={totalPrice}
      imageUrl={firstImage}
      isValid={isValid}
      hideActions={isNoLongerAvailable}
      onRemove={removeInstallServices}
      variant={variant}
    />
  );
};

const renderAttachedNonInstallService = (
  lineId: string,
  variant: AttachedServiceVariant,
  attachedService: AttachedServiceProps | undefined,
  shouldRenderDefaultAttachedService: boolean,
  removeAttachedService?: () => void
) => {
  if (!shouldRenderDefaultAttachedService || !attachedService) return null;

  return <AttachedService key={lineId} {...attachedService} onRemove={removeAttachedService} variant={variant} />;
};

export const AttachedServicesBlock = ({
  lineId,
  variant,
  isNoLongerAvailable,
  attachedInstallServices,
  attachedService,
  shouldRenderDefaultAttachedService,
  removeInstallServices,
  removeAttachedService,
  servicesModuleConfig,
}: AttachedServicesBlockProps) => {
  const installsModule = renderInstallModule(servicesModuleConfig);
  const servicesModule = renderServicesModule(servicesModuleConfig);

  const attachedInstallService = renderAttachedInstallService(
    lineId,
    variant,
    isNoLongerAvailable,
    attachedInstallServices,
    removeInstallServices
  );
  const attachedNonInstallService = renderAttachedNonInstallService(
    lineId,
    variant,
    attachedService,
    shouldRenderDefaultAttachedService,
    removeAttachedService
  );

  const appendedServices = attachedInstallService ? servicesModule : attachedNonInstallService ? installsModule : null;

  if (!attachedInstallService && !attachedNonInstallService && !appendedServices) {
    return null;
  }

  return (
    <div className={styles.attachedServices}>
      {attachedInstallService}
      {attachedNonInstallService}
      {appendedServices ? (
        <div className={styles.appendedServices}>
          <b>Do you need?</b>
          {appendedServices}
        </div>
      ) : null}
    </div>
  );
};
